import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaHeart, FaRegHeart, FaComment, FaShare, FaPlay, FaSpinner, FaChevronUp, FaChevronDown, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../services/api';
import AppBar from '../components/AppBar';
import toast from 'react-hot-toast';
import Hls from 'hls.js';

const Container = styled.div`
  height: 100vh;
  background-color: #000;
  overflow: hidden;
  position: relative;
`;

const CarouselContainer = styled.div`
  height: 100vh;
  position: relative;
  overflow: hidden;
  background-color: #000;
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  transform: translateY(${props => props.index * 100}%);
  transition: transform 0.3s ease;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PostCard = styled.div`
  position: relative;
  height: calc(100vh - 100px);
  width: calc((100vh - 100px) * 9 / 16);
  max-width: 90vw;
  background-color: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  margin: 20px auto 80px auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const Media = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  border-radius: 12px;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  border-radius: 12px;
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 30px 12px 16px;
  z-index: 2;
  min-height: 30vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: 0 0 12px 12px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  width: 100%;
`;

const UserLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
`;

const UserName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const TimeAgo = styled.div`
  color: #ccc;
  font-size: 12px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const Content = styled.div`
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  max-width: 85%;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #fff;
  font-size: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const Action = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
`;

const ActionIcon = styled.div`
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const ActionCount = styled.div`
  font-size: 10px;
  font-weight: 600;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${PostCard}:hover & {
    opacity: 1;
  }
`;

const Duration = styled.div`
  position: absolute;
  bottom: 20px;
  right: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  z-index: 3;
`;

const LoadingContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  color: #fff;
`;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
`;

const EmptyState = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #000;
  color: #fff;
  text-align: center;
  padding: 20px;
`;

const EmptyTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const EmptySubtitle = styled.div`
  font-size: 16px;
  color: #ccc;
`;

const ProgressIndicator = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  z-index: 1000;
`;

const SwipeHint = styled.div`
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 12px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  animation: fadeInOut 3s ease-in-out infinite;
  
  @keyframes fadeInOut {
    0%, 100% { opacity: 0.7; }
    50% { opacity: 1; }
  }
`;

const NavigationArrows = styled.div`
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 1000;
`;

const ArrowButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

const MuteButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 80px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  z-index: 3;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  }
`;

const LoopsScreen = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const carouselRef = useRef(null);
  const videoRefs = useRef({});

  const fetchLoops = async () => {
    try {
      setLoading(true);
      const response = await postsAPI.getPosts(1, 50, 'loops');
      const loopsPosts = Array.isArray(response) ? response : (response.posts || []);
      console.log('🎬 Loops posts:', loopsPosts);
      setPosts(loopsPosts);
    } catch (error) {
      console.error('Error fetching loops:', error);
      toast.error('Failed to load loops');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoops();
  }, []);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleLike = async (postId, index) => {
    try {
      const post = posts[index];
      const isLiked = post.is_liked || false;
      
      if (isLiked) {
        await postsAPI.unlikePost(postId);
        setPosts(prev => prev.map((p, i) => 
          i === index ? { ...p, is_liked: false, likes_count: (p.likes_count || 1) - 1 } : p
        ));
      } else {
        await postsAPI.likePost(postId);
        setPosts(prev => prev.map((p, i) => 
          i === index ? { ...p, is_liked: true, likes_count: (p.likes_count || 0) + 1 } : p
        ));
      }
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleVideoPlay = (postId, index) => {
    setPlayingVideos(prev => new Set([...prev, postId]));
  };

  const handleVideoPause = (postId) => {
    setPlayingVideos(prev => {
      const newSet = new Set(prev);
      newSet.delete(postId);
      return newSet;
    });
  };

  // Auto-play video when it becomes current
  useEffect(() => {
    if (posts.length > 0 && currentIndex < posts.length) {
      const currentPost = posts[currentIndex];
      const mainMedia = currentPost.media && currentPost.media.length > 0 ? currentPost.media[0] : null;
      const isVideo = mainMedia && (
        mainMedia.type === 'video' || 
        mainMedia.path?.includes('.mp4') || 
        mainMedia.path?.includes('.mov') || 
        mainMedia.path?.includes('.m3u8')
      );

      // Pause all videos first
      Object.keys(videoRefs.current).forEach(postId => {
        if (videoRefs.current[postId]) {
          videoRefs.current[postId].pause();
          handleVideoPause(postId);
        }
      });

              // Play current video if it's a video
        if (isVideo && videoRefs.current[currentPost.id]) {
          const videoElement = videoRefs.current[currentPost.id];
          
          // Reset video to beginning
          videoElement.currentTime = 0;
          
          // Set mute state
          videoElement.muted = isMuted;
          
          // Handle HLS videos
          if (mainMedia.path?.includes('.m3u8')) {
            if (Hls.isSupported()) {
              const hls = new Hls();
              hls.loadSource(mainMedia.path);
              hls.attachMedia(videoElement);
              hls.on(Hls.Events.MANIFEST_PARSED, () => {
                videoElement.play().then(() => {
                  handleVideoPlay(currentPost.id, currentIndex);
                }).catch(error => {
                  console.log('HLS auto-play failed:', error);
                });
              });
            } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
              // Native HLS support (Safari)
              videoElement.play().then(() => {
                handleVideoPlay(currentPost.id, currentIndex);
              }).catch(error => {
                console.log('Native HLS auto-play failed:', error);
              });
            }
          } else {
            // Regular video files
            videoElement.play().then(() => {
              handleVideoPlay(currentPost.id, currentIndex);
            }).catch(error => {
              console.log('Auto-play failed:', error);
            });
          }
        }
    }
  }, [currentIndex, posts]);

  const goToNext = () => {
    if (currentIndex < posts.length - 1 && !isNavigating) {
      setIsNavigating(true);
      setCurrentIndex(prev => prev + 1);
      setTimeout(() => setIsNavigating(false), 300);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0 && !isNavigating) {
      setIsNavigating(true);
      setCurrentIndex(prev => prev - 1);
      setTimeout(() => setIsNavigating(false), 300);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const deltaY = startY - currentY;
    const threshold = 50; // Minimum swipe distance
    
    if (Math.abs(deltaY) > threshold) {
      if (deltaY > 0) {
        // Swipe up - next post
        goToNext();
      } else {
        // Swipe down - previous post
        goToPrevious();
      }
    }
    
    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartY(e.clientY);
    setCurrentY(e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setCurrentY(e.clientY);
  };

  const handleMouseUp = (e) => {
    if (!isDragging) return;
    
    const deltaY = startY - currentY;
    const threshold = 50; // Minimum swipe distance
    
    if (Math.abs(deltaY) > threshold) {
      if (deltaY > 0) {
        // Swipe up - next post
        goToNext();
      } else {
        // Swipe down - previous post
        goToPrevious();
      }
    }
    
    setIsDragging(false);
    setStartY(0);
    setCurrentY(0);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    
    if (isNavigating) return;
    
    // Debounce wheel events
    if (e.deltaY > 0) {
      // Scroll down - next post
      goToNext();
    } else if (e.deltaY < 0) {
      // Scroll up - previous post
      goToPrevious();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    
    // Apply mute state to all videos
    Object.keys(videoRefs.current).forEach(postId => {
      if (videoRefs.current[postId]) {
        videoRefs.current[postId].muted = !isMuted;
      }
    });
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        goToNext();
        break;
      case 'ArrowDown':
        e.preventDefault();
        goToPrevious();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, posts.length]);

  if (loading) {
    return (
      <Container>
        <AppBar />
        <LoadingContainer>
          <LoadingText>
            <FaSpinner className="animate-spin" />
            Loading loops...
          </LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  if (posts.length === 0) {
    return (
      <Container>
        <AppBar />
        <EmptyState>
          <EmptyTitle>No loops available</EmptyTitle>
          <EmptySubtitle>Check back later for new content!</EmptySubtitle>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <AppBar />
      <CarouselContainer 
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {/* Progress Indicator */}
        <ProgressIndicator>
          {currentIndex + 1} / {posts.length}
        </ProgressIndicator>
        

        
        {/* Swipe Hint - only on first slide */}
        {posts.length > 1 && currentIndex === 0 && (
          <SwipeHint>
            Swipe up for next post
          </SwipeHint>
        )}
        
        {/* Navigation Arrows */}
        {posts.length > 1 && (
          <NavigationArrows>
            <ArrowButton
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              title="Previous post"
            >
              <FaChevronUp />
            </ArrowButton>
            <ArrowButton
              onClick={goToNext}
              disabled={currentIndex === posts.length - 1}
              title="Next post"
            >
              <FaChevronDown />
            </ArrowButton>
          </NavigationArrows>
        )}
        {posts.map((post, index) => {
          const mainMedia = post.media && post.media.length > 0 ? post.media[0] : null;
          const isVideo = mainMedia && (
            mainMedia.type === 'video' || 
            mainMedia.path?.includes('.mp4') || 
            mainMedia.path?.includes('.mov') || 
            mainMedia.path?.includes('.m3u8')
          );
          const isPlaying = playingVideos.has(post.id);
          const isCurrentPost = index === currentIndex;

          return (
            <Slide key={post.id} index={index - currentIndex}>
              <PostCard>
                {/* Media Background */}
                {mainMedia && !isVideo && (
                  <Media src={mainMedia.path} alt="Post media" />
                )}
                
                {mainMedia && isVideo && (
                  <>
                    <Video
                      ref={el => videoRefs.current[post.id] = el}
                      src={mainMedia.path}
                      poster={mainMedia.cover}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      onPlay={() => handleVideoPlay(post.id, index)}
                      onPause={() => handleVideoPause(post.id)}
                    />
                    
                    {/* Play Button for Videos */}
                    {!isPlaying && (
                      <PlayButton>
                        <FaPlay style={{ color: '#000', fontSize: '24px' }} />
                      </PlayButton>
                    )}
                    
                    {/* Duration Badge for Videos */}
                    {mainMedia.duration && (
                      <Duration>
                        {formatDuration(mainMedia.duration)}
                      </Duration>
                    )}
                    
                    {/* Mute Button */}
                    <MuteButton onClick={toggleMute} title={isMuted ? "Unmute" : "Mute"}>
                      {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                    </MuteButton>
                  </>
                )}
                
                {/* Overlay with User Info and Actions */}
                <Overlay>
                  <UserInfo>
                    <UserLeft>
                      <Avatar
                        src={post.user?.meta?.profile_image || '/default-avatar.png'}
                        alt={post.user?.meta?.name || post.user?.username}
                      />
                      <div>
                        <UserName>@{post.user?.username || 'unknown'}</UserName>
                        {post.timeago && <TimeAgo>{post.timeago}</TimeAgo>}
                      </div>
                    </UserLeft>
                  </UserInfo>
                  
                  {post.content && <Content>{post.content}</Content>}
                  
                  <Actions>
                    <Action onClick={() => handleLike(post.id, index)}>
                      <ActionIcon>
                        {post.is_liked ? (
                          <FaHeart style={{ color: '#ff4d4f' }} />
                        ) : (
                          <FaRegHeart />
                        )}
                      </ActionIcon>
                      <ActionCount>{post.likes_count || 0}</ActionCount>
                    </Action>
                    
                    <Action>
                      <ActionIcon>
                        <FaComment />
                      </ActionIcon>
                      <ActionCount>{post.comments_count || 0}</ActionCount>
                    </Action>
                    
                    <Action>
                      <ActionIcon>
                        <FaShare />
                      </ActionIcon>
                      <ActionCount>Share</ActionCount>
                    </Action>
                  </Actions>
                </Overlay>
              </PostCard>
            </Slide>
          );
        })}
      </CarouselContainer>
    </Container>
  );
};

export default LoopsScreen; 